import React, { useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TextField,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import { StudentSearch } from "../common/StudentSearch";
import { getStudentNotificationHistory } from "../../api/endpoints/individualMessages";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import { getResponsablesByStudent } from "../../api/endpoints/users";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR");
};

export const IndividualMessagesHistory = () => {
  const auth = useContext(AuthContext);
  const unit = auth?.user?.unit || "PATOS";

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyResult, setHistoryResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [recipientType, setRecipientType] = useState("STUDENT");
  const [responsables, setResponsables] = useState([]);
  const [selectedResponsable, setSelectedResponsable] = useState(null);

  const summary = useMemo(() => {
    if (!historyResult) return null;
    return `Aluno: ${historyResult.student?.name || "-"} (${historyResult.student?.idSponte || "-"}) | Total: ${historyResult.totalMessages || 0} | Visualizadas: ${historyResult.readMessages || 0} | Não visualizadas: ${historyResult.unreadMessages || 0}`;
  }, [historyResult]);

  const handleSearchHistory = async () => {
    if (!selectedStudent?.AlunoID) {
      setErrorMessage("Selecione um aluno para consultar o histórico.");
      return;
    }

    if (recipientType === "RESPONSABLE" && !selectedResponsable?.idSponte) {
      setErrorMessage("Selecione um responsável para consultar as mensagens.");
      return;
    }

    try {
      setErrorMessage("");
      setHistoryLoading(true);
      const result = await getStudentNotificationHistory(
        String(selectedStudent.AlunoID),
        unit,
        recipientType === "RESPONSABLE" ? "RESPONSABLE" : "STUDENT",
        selectedResponsable?.idSponte || null
      );
      setHistoryResult(result);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error || "Não foi possível consultar o histórico."
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleStudentSelect = async (student) => {
    setSelectedStudent(student);
    setHistoryResult(null);
    setSelectedResponsable(null);
    setResponsables([]);
    setErrorMessage("");

    if (!student?.AlunoID) return;
    try {
      const data = await getResponsablesByStudent(String(student.AlunoID), unit);
      setResponsables(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao buscar responsáveis para filtro:", error);
      setResponsables([]);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <FormControl fullWidth>
        <InputLabel id="history-recipient-type-label">Tipo de Destinatário</InputLabel>
        <Select
          labelId="history-recipient-type-label"
          value={recipientType}
          label="Tipo de Destinatário"
          onChange={(e) => {
            setRecipientType(e.target.value);
            setHistoryResult(null);
            setSelectedStudent(null);
            setResponsables([]);
            setSelectedResponsable(null);
            setErrorMessage("");
          }}
        >
          <MenuItem value="STUDENT">Aluno Específico</MenuItem>
          <MenuItem value="RESPONSABLE">Responsável Específico</MenuItem>
        </Select>
      </FormControl>

      <StudentSearch
        onSelect={handleStudentSelect}
        label="Digite o nome do aluno para consulta (mín. 3 caracteres)"
      />

      {recipientType === "RESPONSABLE" && (
        <Box sx={{ minWidth: 320, flex: 1 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
            Responsável do aluno selecionado
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            value={selectedResponsable?.idSponte || ""}
            onChange={(e) => {
              const found = responsables.find(
                (item) => String(item.idSponte) === String(e.target.value)
              );
              setSelectedResponsable(found || null);
            }}
            disabled={!selectedStudent || responsables.length === 0}
            placeholder="Selecione um responsável"
            SelectProps={{ native: false }}
          >
            {responsables.map((resp) => (
              <MenuItem key={resp.idSponte} value={resp.idSponte}>
                {resp.name} ({resp.idSponte})
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          onClick={handleSearchHistory}
          disabled={historyLoading}
          sx={{ minWidth: 240 }}
        >
          {historyLoading ? "Consultando..." : "Consultar histórico"}
        </Button>
      </Box>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      {summary && <Alert severity="info">{summary}</Alert>}

      {historyResult && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Destinatário</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Enviada em</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Mensagem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(historyResult.messages || []).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.title || "-"}</TableCell>
                  <TableCell>{item.recipient_name || "-"}</TableCell>
                  <TableCell>
                    {item.recipient_type === "RESPONSABLE" ? "Responsável" : "Aluno"}
                  </TableCell>
                  <TableCell>{formatDateTime(item.sent_at)}</TableCell>
                  <TableCell>
                    {item.read_at ? (
                      <Chip size="small" color="success" label="Visualizada" />
                    ) : (
                      <Chip size="small" color="warning" label="Não visualizada" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Abrir mensagem">
                      <IconButton onClick={() => setSelectedMessage(item)} size="small">
                        <MessageOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {(!historyResult.messages || historyResult.messages.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nenhuma mensagem encontrada para este aluno.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{selectedMessage?.title || "Mensagem"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
            <Typography variant="body2">
              <strong>Destinatário:</strong> {selectedMessage?.recipient_name || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>Tipo:</strong>{" "}
              {selectedMessage?.recipient_type === "RESPONSABLE" ? "Responsável" : "Aluno"}
            </Typography>
            <Typography variant="body2">
              <strong>Enviada em:</strong> {formatDateTime(selectedMessage?.sent_at)}
            </Typography>
            <Typography variant="body2">
              <strong>Visualizada em:</strong> {formatDateTime(selectedMessage?.read_at)}
            </Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
              {selectedMessage?.message || "-"}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedMessage(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

